/* Progressive, form-free PDP side panels. No window globals, content cloning,
   third-party calls, or changes to native product/variant/app handlers. */
(() => {
  'use strict';

  const runtimeAttribute = 'data-lk-product-panels-runtime';
  const mountEvent = 'lk:product-panels:mount';
  if (document.documentElement.hasAttribute(runtimeAttribute)) {
    // Repeated snippet assets (or a newly injected section) reuse one runtime.
    document.dispatchEvent(new CustomEvent(mountEvent));
    return;
  }

  const rootSelector = '[data-lk-product-panels]';
  const controllers = new Map();
  const lockOwners = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = [
    'a[href]', 'button:not([disabled])', 'summary', 'video[controls]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const acquireLock = (owner) => {
    lockOwners.add(owner);
    document.body.setAttribute('data-lk-product-panels-locked', '');
  };

  const releaseLock = (owner) => {
    lockOwners.delete(owner);
    if (!lockOwners.size) document.body.removeAttribute('data-lk-product-panels-locked');
    // Never overwrite overflow/position/padding or remove another modal's class.
  };

  class ProductPanels {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-lk-panel-dialog]');
      this.title = root.querySelector('[data-lk-panel-dialog-title]');
      this.body = root.querySelector('[data-lk-panel-dialog-body]');
      this.closeButton = root.querySelector('[data-lk-panel-close]');
      this.current = null;
      this.returnFocusOnClose = true;
      this.backdropPress = false;
      this.destroyed = false;
      this.triggers = Array.from(root.querySelectorAll('[data-lk-panel-trigger]'))
        .filter((trigger) => trigger.closest(rootSelector) === root);
      this.supported = !!(this.dialog && this.title && this.body && this.closeButton &&
        typeof this.dialog.showModal === 'function' && typeof this.dialog.close === 'function');
      if (!this.supported) return; // Native details remain fully functional.

      this.onRootClick = (event) => {
        const trigger = event.target instanceof Element
          ? event.target.closest('[data-lk-panel-trigger]') : null;
        if (!trigger || trigger.closest(rootSelector) !== this.root || event.defaultPrevented ||
            event.button !== 0) return;
        const panel = trigger.parentElement;
        if (!panel.matches('details[data-lk-panel]')) return;
        if (this.open(panel, trigger)) event.preventDefault();
      };
      this.onPointerDown = (event) => { this.backdropPress = this.isBackdrop(event); };
      this.onDialogClick = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target && target.closest('[data-lk-panel-close]') === this.closeButton) {
          this.close();
        } else if (this.backdropPress && this.isBackdrop(event)) {
          this.close();
        }
        this.backdropPress = false;
      };
      this.onCancel = (event) => {
        event.preventDefault();
        this.close();
      };
      this.onClose = () => {
        // A queued close event must not tear down a subsequently reopened panel.
        if (!this.dialog.open) this.finishClose(this.returnFocusOnClose);
      };
      this.onKeyDown = (event) => this.trapFocus(event);

      this.root.addEventListener('click', this.onRootClick);
      this.dialog.addEventListener('pointerdown', this.onPointerDown);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('keydown', this.onKeyDown);
      this.root.setAttribute('data-lk-panels-enhanced', '');
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom;
    }

    open(panel, trigger) {
      if (this.destroyed) return false;
      // An external dialog.close() queues its close event. Restore the current
      // node before looking up the next panel, including a same-turn reopen.
      if (this.current) this.restoreContent(false);
      const content = Array.from(panel.children).find((node) => node.hasAttribute('data-lk-panel-content'));
      if (!content) return false;
      const marker = document.createComment('lk-panel-content-origin');
      content.before(marker);
      this.current = {panel, trigger, content, marker};
      this.title.textContent = trigger.querySelector('[data-lk-panel-label]').textContent.trim();
      this.body.append(content); // Move, never clone: no duplicate IDs/content/apps.
      this.returnFocusOnClose = true;
      try {
        if (!this.dialog.open) this.dialog.showModal();
      } catch (_) {
        this.restoreContent(false);
        this.destroy(false);
        controllers.delete(this.root);
        return false; // Do not cancel the summary's native default behavior.
      }
      panel.open = false;
      trigger.setAttribute('aria-expanded', 'true');
      acquireLock(this);
      this.body.scrollTop = 0;
      this.closeButton.focus({preventScroll: true});
      if (!reducedMotion.matches) {
        content.querySelectorAll('[data-lk-panel-video]').forEach((video) => {
          video.muted = true;
          const playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(() => {});
        });
      }
      return true;
    }

    pauseVideos() {
      if (this.current) {
        this.current.content.querySelectorAll('video').forEach((video) => video.pause());
      }
    }

    restoreContent(returnFocus) {
      if (!this.current) return;
      const {trigger, content, marker} = this.current;
      this.pauseVideos();
      if (marker.parentNode) marker.replaceWith(content);
      trigger.setAttribute('aria-expanded', 'false');
      this.current = null;
      if (returnFocus && trigger.isConnected && !trigger.closest('[inert]') &&
          !document.querySelector('dialog[open]')) {
        trigger.focus({preventScroll: true});
      }
    }

    finishClose(returnFocus) {
      this.restoreContent(returnFocus);
      releaseLock(this);
      this.backdropPress = false;
    }

    close(returnFocus = true) {
      this.returnFocusOnClose = returnFocus;
      if (this.dialog.open) this.dialog.close();
      this.finishClose(returnFocus);
    }

    trapFocus(event) {
      // Also handle Escape explicitly: embedded browsers may deliver keydown
      // without the dialog's native cancel default action. Scope it to this modal.
      if (event.key === 'Escape' && this.dialog.open) {
        event.preventDefault();
        event.stopPropagation(); // Do not also dismiss a lower cart/app modal.
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.dialog.open) return;
      event.stopPropagation(); // Only the active dialog owns this focus cycle.
      const items = Array.from(this.dialog.querySelectorAll(focusableSelector)).filter((node) =>
        node.tabIndex >= 0 && node.getClientRects().length && !node.closest('[inert]') &&
        window.getComputedStyle(node).visibility !== 'hidden');
      if (!items.length) {
        event.preventDefault();
        this.closeButton.focus({preventScroll: true});
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.dialog)) {
        event.preventDefault();
        last.focus({preventScroll: true});
      } else if (!event.shiftKey && (active === last || active === this.dialog)) {
        event.preventDefault();
        first.focus({preventScroll: true});
      }
    }

    destroy(returnFocus = false) {
      if (this.destroyed) return;
      this.destroyed = true;
      if (!this.supported) return;
      this.close(returnFocus);
      this.root.removeEventListener('click', this.onRootClick);
      this.dialog.removeEventListener('pointerdown', this.onPointerDown);
      this.dialog.removeEventListener('click', this.onDialogClick);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('keydown', this.onKeyDown);
      this.root.removeAttribute('data-lk-panels-enhanced');
      this.triggers.forEach((trigger) => {
        trigger.removeAttribute('aria-haspopup');
        trigger.removeAttribute('aria-controls');
        trigger.removeAttribute('aria-expanded');
      });
    }
  }

  const mount = (container = document) => {
    controllers.forEach((controller, root) => {
      if (!root.isConnected) {
        controller.destroy(false);
        controllers.delete(root);
      }
    });
    const roots = Array.from(container.querySelectorAll(rootSelector));
    if (container instanceof Element && container.matches(rootSelector)) roots.unshift(container);
    roots.forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, new ProductPanels(root));
    });
  };

  document.addEventListener(mountEvent, () => mount());
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller, root) => {
      if (event.target === root || event.target.contains(root)) {
        controller.destroy(false);
        controllers.delete(root);
      }
    });
  });
  const onMotionChange = () => {
    if (reducedMotion.matches) controllers.forEach((controller) => controller.pauseVideos());
  };
  if (typeof reducedMotion.addEventListener === 'function') {
    reducedMotion.addEventListener('change', onMotionChange);
  } else if (typeof reducedMotion.addListener === 'function') {
    reducedMotion.addListener(onMotionChange);
  }
  window.addEventListener('pagehide', () => {
    controllers.forEach((controller) => controller.destroy(false));
    controllers.clear();
  });
  window.addEventListener('pageshow', () => mount());
  document.documentElement.setAttribute(runtimeAttribute, '');
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), {once: true});
  } else {
    mount();
  }
})();